import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowRight,
  Gem,
  Sparkles,
  Trophy,
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTheme } from "../contexts/ThemeContext";
import ApiURl from "../controllers/Api";

gsap.registerPlugin(ScrollTrigger);

const MAROON = "#2B0000";
const MAROON_DARK = "#190000";
const MAROON_SOFT = "#4A0808";
const GOLD = "#D6B33F";
const GOLD_LIGHT = "#E9CD7A";
const CREAM = "#FBF6EF";
const WHITE = "#FFFDF9";
const TEXT_SOFT = "#70635E";

const galleryImages = [
  {
    src: "https://images.unsplash.com/photo-1560523159-4a9692d222ef?q=85&w=1200&auto=format&fit=crop",
    category: "Award Ceremonies",
    title: "Annual Excellence Awards",
  },
  {
    src: "https://images.unsplash.com/photo-1507501336603-6e31db2be093?q=85&w=1200&auto=format&fit=crop",
    category: "Award Ceremonies",
    title: "Recognition Evening",
  },
  {
    src: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=85&w=1200&auto=format&fit=crop",
    category: "Brand Events",
    title: "Luxury Brand Celebration",
  },
  {
    src: "https://images.unsplash.com/photo-1505236858219-8359eb29e329?q=85&w=1200&auto=format&fit=crop",
    category: "Exhibitions",
    title: "Jewellery Exhibition",
  },
  {
    src: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=85&w=1200&auto=format&fit=crop",
    category: "Award Ceremonies",
    title: "Business Leadership Event",
  },
  {
    src: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?q=85&w=1200&auto=format&fit=crop",
    category: "Brand Events",
    title: "Exclusive Brand Night",
  },
  {
    src: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?q=85&w=1200&auto=format&fit=crop",
    category: "Exhibitions",
    title: "Premium Jewellery Showcase",
  },
  {
    src: "https://images.unsplash.com/photo-1507501336603-6e31db2be093?q=85&w=1200&auto=format&fit=crop",
    category: "Brand Events",
    title: "Grand Store Celebration",
  },
  {
    src: "https://images.unsplash.com/photo-1531058020387-3be344556be6?q=85&w=1200&auto=format&fit=crop",
    category: "Exhibitions",
    title: "Designer Collection Launch",
  },
  {
    src: "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?q=85&w=1200&auto=format&fit=crop",
    category: "Award Ceremonies",
    title: "Achievement Celebration",
  },
];

const filters = [
  "All Events",
  "Award Ceremonies",
  "Exhibitions",
  "Brand Events",
];

export default function Gallery() {
  const { theme } = useTheme();

  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const heroContentRef = useRef(null);
  const trophyRef = useRef(null);
  const awardsRef = useRef(null);
  const galleryRef = useRef(null);
  const ctaRef = useRef(null);

  const [activeFilter, setActiveFilter] = useState("All Events");
  const [awards, setAwards] = useState([]);

  useEffect(() => {
    let mounted = true;

    fetch(`${ApiURl}getAwards.php?active_only=1`)
      .then((response) => response.json())
      .then((response) => {
        if (!mounted || !response?.success || !Array.isArray(response.data)) return;
        setAwards(response.data || []);
      })
      .catch((error) => {
        console.error("Error loading awards:", error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const visibleAwards = useMemo(
    () => awards.filter((award) => award.image_url),
    [awards]
  );

  const filteredImages = useMemo(() => {
    if (activeFilter === "All Events") {
      return galleryImages;
    }

    return galleryImages.filter(
      (image) => image.category === activeFilter
    );
  }, [activeFilter]);

  useLayoutEffect(() => {
    const context = gsap.context(() => {
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (reduceMotion) {
        gsap.set(
          [
            heroContentRef.current,
            trophyRef.current,
            awardsRef.current,
            galleryRef.current,
            ctaRef.current,
          ],
          {
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1,
          }
        );

        return;
      }

      const heroTimeline = gsap.timeline({
        defaults: {
          ease: "power3.out",
        },
      });

      heroTimeline
        .fromTo(
          heroRef.current,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.5,
          }
        )
        .fromTo(
          heroContentRef.current,
          {
            opacity: 0,
            x: -60,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1,
          },
          "-=0.2"
        )
        .fromTo(
          trophyRef.current,
          {
            opacity: 0,
            x: 70,
            y: 25,
            rotate: 5,
            scale: 0.88,
          },
          {
            opacity: 1,
            x: 0,
            y: 0,
            rotate: 0,
            scale: 1,
            duration: 1.1,
          },
          "-=0.75"
        );

      gsap.to(trophyRef.current, {
        y: -14,
        duration: 2.6,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });

      gsap.fromTo(
        ".award-card",
        {
          opacity: 0,
          y: 45,
          scale: 0.96,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.75,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: awardsRef.current,
            start: "top 82%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        ".gallery-heading-content",
        {
          opacity: 0,
          x: -45,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: galleryRef.current,
            start: "top 84%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        ".gallery-filter-buttons",
        {
          opacity: 0,
          x: 45,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: galleryRef.current,
            start: "top 84%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.fromTo(
        ctaRef.current,
        {
          opacity: 0,
          y: 40,
          scale: 0.98,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ctaRef.current,
            start: "top 90%",
            toggleActions: "play none none reverse",
          },
        }
      );

      gsap.to(".hero-background-pattern", {
        xPercent: 8,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, pageRef);

    return () => context.revert();
  }, []);

  useLayoutEffect(() => {
    const context = gsap.context(() => {
      const cards = gsap.utils.toArray(".event-gallery-card");

      gsap.fromTo(
        cards,
        {
          opacity: 0,
          y: 30,
          scale: 0.97,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.65,
          stagger: 0.07,
          ease: "power3.out",
        }
      );
    }, galleryRef);

    return () => context.revert();
  }, [activeFilter]);

  const pageBackground = theme?.user_bg || CREAM;
  const pageText = theme?.user_text || MAROON_DARK;
  const softText = theme?.user_text_soft || TEXT_SOFT;
  const borderColor = theme?.user_border || "#E9DCD2";

  return (
    <main
      ref={pageRef}
      className="min-h-screen overflow-hidden"
      style={{
        backgroundColor: pageBackground,
        color: pageText,
      }}
    >
      {/* Hero section */}
      <section
        ref={heroRef}
        className="
          relative min-h-[390px] overflow-hidden
          border-b sm:min-h-[430px]
          lg:min-h-[360px]
        "
        style={{
          borderColor,
          background:
            "linear-gradient(105deg, #FFFDF9 0%, #FBF6EF 45%, #F2E3D8 100%)",
        }}
      >
        <div
          className="
            hero-background-pattern pointer-events-none
            absolute inset-0 opacity-60
          "
        >
          <div
            className="
              absolute -right-24 -top-40 h-[520px] w-[520px]
              rounded-full blur-3xl
            "
            style={{
              backgroundColor: "rgba(214,179,63,0.12)",
            }}
          />

          <div
            className="
              absolute -bottom-52 right-[18%] h-[500px] w-[500px]
              rounded-full blur-3xl
            "
            style={{
              backgroundColor: "rgba(43,0,0,0.09)",
            }}
          />

          <div
            className="
              absolute left-[46%] top-1/2 h-[1px] w-[60%]
              -rotate-12
            "
            style={{
              background:
                "linear-gradient(to right, transparent, rgba(214,179,63,0.35), transparent)",
            }}
          />
        </div>

        <div
          className="
            relative z-10 mx-auto grid min-h-[390px]
            max-w-[1440px] items-center gap-8
            px-5 py-12
            sm:min-h-[430px] sm:px-8 sm:py-14
            lg:min-h-[360px] lg:grid-cols-[1.1fr_0.9fr]
            lg:px-14 lg:py-12
            xl:px-20
          "
        >
          <div
            ref={heroContentRef}
            className="relative z-20 max-w-[680px]"
          >
            <div
              className="
                mb-4 inline-flex items-center gap-2
                rounded-full border bg-white/60
                px-3 py-1.5 text-[10px] font-semibold
                uppercase tracking-[0.26em]
                backdrop-blur-sm sm:text-xs
              "
              style={{
                borderColor: "rgba(214,179,63,0.35)",
                color: MAROON,
              }}
            >
              <Sparkles size={14} style={{ color: GOLD }} />
              Celebrating Excellence
            </div>

            <h1
              className="
                max-w-[680px] font-serif
                text-[41px] leading-[1.06]
                sm:text-6xl
                lg:text-[64px]
              "
              style={{ color: MAROON_DARK }}
            >
              Awards &amp; Achievements
            </h1>

            <div className="mt-5 flex items-center gap-3">
              <span
                className="h-px w-20"
                style={{ backgroundColor: GOLD }}
              />

              <span
                className="
                  flex h-7 w-7 rotate-45 items-center
                  justify-center rounded-md border bg-white
                "
                style={{ borderColor: `${GOLD}80` }}
              >
                <Gem
                  size={13}
                  className="-rotate-45"
                  style={{ color: GOLD }}
                />
              </span>

              <span
                className="h-px w-20"
                style={{ backgroundColor: GOLD }}
              />
            </div>

            <p
              className="
                mt-5 max-w-[620px] text-sm leading-7
                sm:text-[15px] sm:leading-8
              "
              style={{ color: softText }}
            >
              Honouring our commitment to excellence, craftsmanship and
              trust. These recognitions inspire us to shine even brighter.
            </p>
          </div>

          {/* Trophy visual */}
          <div
            className="
              relative hidden h-[300px] items-end
              justify-center lg:flex
            "
          >
            <div
              className="
                absolute bottom-[-80px] right-[-90px]
                h-[360px] w-[600px]
                rotate-[-7deg] rounded-[50%]
              "
              style={{
                background:
                  "radial-gradient(circle at 45% 25%, #720A14 0%, #390006 45%, #160000 78%)",
                boxShadow:
                  "inset 0 30px 60px rgba(255,255,255,0.05), 0 25px 70px rgba(43,0,0,0.2)",
              }}
            />

            <div
              className="
                absolute bottom-[45px] right-[-70px]
                h-[90px] w-[600px]
                rotate-[-11deg] rounded-[50%]
                border-t border-white/10
              "
            />

            <div
              ref={trophyRef}
              className="
                relative z-10 flex flex-col items-center
                drop-shadow-[0_25px_25px_rgba(43,0,0,0.35)]
              "
            >
              <div
                className="
                  flex h-32 w-32 items-center justify-center
                  rounded-full border-[6px]
                  shadow-[inset_0_0_25px_rgba(255,255,255,0.55)]
                "
                style={{
                  borderColor: "#C9941F",
                  background:
                    "linear-gradient(145deg, #FFF0A6 0%, #D89A20 40%, #8C4D06 100%)",
                }}
              >
                <Trophy
                  size={78}
                  strokeWidth={1.3}
                  style={{ color: "#6E3300" }}
                />
              </div>

              <div
                className="
                  h-20 w-10
                  bg-gradient-to-r
                  from-[#8C4D06] via-[#F1C34B] to-[#8C4D06]
                "
              />

              <div
                className="
                  flex h-20 w-36 flex-col items-center
                  justify-center rounded-t-lg border
                  shadow-xl
                "
                style={{
                  borderColor: "#C9941F",
                  background:
                    "linear-gradient(145deg, #391000, #130000)",
                }}
              >
                <span
                  className="font-serif text-3xl font-semibold"
                  style={{ color: GOLD_LIGHT }}
                >
                  SL
                </span>

                <span
                  className="
                    text-[9px] uppercase tracking-[0.32em]
                  "
                  style={{ color: GOLD }}
                >
                  Gold
                </span>
              </div>

              <div
                className="
                  h-4 w-44 rounded-b-md
                  bg-gradient-to-r
                  from-[#090000] via-[#541007] to-[#090000]
                "
              />
            </div>
          </div>

          {/* Mobile trophy */}
          <div
            className="
              pointer-events-none absolute bottom-[-40px]
              right-[-40px] flex h-[240px] w-[240px]
              items-center justify-center rounded-full
              lg:hidden
            "
            style={{
              background:
                "radial-gradient(circle at 40% 30%, #720A14, #250003 72%)",
            }}
          >
            <Trophy
              size={115}
              strokeWidth={1.05}
              style={{ color: GOLD }}
              className="opacity-80"
            />
          </div>
        </div>
      </section>

      {/* Awards section */}
      <section
        ref={awardsRef}
        className="
          relative border-b px-4 py-11
          sm:px-8 sm:py-14
          lg:px-14 lg:py-16
          xl:px-20
        "
        style={{ borderColor }}
      >
        <div className="mx-auto max-w-[1440px]">
          <div
            className="
              mb-7 flex items-end justify-between
              gap-5 lg:hidden
            "
          >
            <div>
              <p
                className="
                  text-[10px] font-semibold uppercase
                  tracking-[0.28em]
                "
                style={{ color: GOLD }}
              >
                Our Recognition
              </p>

              <h2
                className="mt-2 font-serif text-3xl"
                style={{ color: MAROON_DARK }}
              >
                Our Awards
              </h2>
            </div>

            <button
              type="button"
              className="
                flex shrink-0 items-center gap-2
                text-xs font-semibold
              "
              style={{ color: MAROON }}
            >
              View All
              <ArrowRight size={15} />
            </button>
          </div>

          <div
            className="
              -mx-4 flex snap-x snap-mandatory
              gap-3 overflow-x-auto px-4 pb-3
              [scrollbar-width:none]
              [&::-webkit-scrollbar]:hidden
              sm:-mx-8 sm:px-8
              lg:mx-0 lg:grid lg:grid-cols-2
              lg:gap-4 lg:overflow-visible lg:px-0
              lg:pb-0
              xl:grid-cols-4
            "
          >
            {visibleAwards.map(
              ({
                id,
                title,
                image_url,
              }) => (
                <article
                  key={id || title}
                  className="
                    award-card group relative
                    min-w-[315px] snap-start overflow-hidden
                    rounded-[18px] border bg-white/55
                    h-[414px] text-center
                    shadow-[0_12px_35px_rgba(43,0,0,0.045)]
                    backdrop-blur-sm
                    transition-all duration-500
                    hover:-translate-y-2
                    hover:shadow-[0_22px_50px_rgba(43,0,0,0.12)]
                    sm:h-[468px] sm:min-w-[345px]
                    lg:h-[414px] lg:min-w-0
                  "
                  style={{ borderColor }}
                >
                  <img
                    src={image_url}
                    alt={title || "SL Gold award"}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />

                </article>
              )
            )}
          </div>
        </div>
      </section>

      {/* Events gallery */}
      <section
        ref={galleryRef}
        className="
          px-4 py-12
          sm:px-8 sm:py-16
          lg:px-14 lg:py-20
          xl:px-20
        "
      >
        <div className="mx-auto max-w-[1440px]">
          <div
            className="
              flex flex-col gap-7
              lg:flex-row lg:items-end
              lg:justify-between
            "
          >
            <div className="gallery-heading-content">
              <p
                className="
                  text-[10px] font-semibold uppercase
                  tracking-[0.28em]
                "
                style={{ color: GOLD }}
              >
                Memorable Moments
              </p>

              <h2
                className="
                  mt-2 font-serif text-4xl
                  sm:text-5xl
                "
                style={{ color: MAROON_DARK }}
              >
                Events Gallery
              </h2>

              <div className="mt-4 flex items-center gap-2">
                <span
                  className="h-px w-16"
                  style={{ backgroundColor: GOLD }}
                />

                <Gem size={13} style={{ color: GOLD }} />

                <span
                  className="h-px w-16"
                  style={{ backgroundColor: GOLD }}
                />
              </div>

              <p
                className="mt-4 text-sm leading-7"
                style={{ color: softText }}
              >
                A glimpse of the moments that define our journey.
              </p>
            </div>

            <div
              className="
                gallery-filter-buttons
                flex gap-2 overflow-x-auto pb-1
                [scrollbar-width:none]
                [&::-webkit-scrollbar]:hidden
              "
            >
              {filters.map((filter) => {
                const active = activeFilter === filter;

                return (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setActiveFilter(filter)}
                    className="
                      shrink-0 rounded-[9px] border
                      px-4 py-2.5 text-xs font-medium
                      transition-all duration-300
                      sm:px-5 sm:py-3
                    "
                    style={{
                      borderColor: active ? MAROON : `${GOLD}90`,
                      backgroundColor: active
                        ? MAROON
                        : "rgba(255,255,255,0.55)",
                      color: active ? "#FFFFFF" : MAROON,
                      boxShadow: active
                        ? "0 10px 24px rgba(43,0,0,0.18)"
                        : "none",
                    }}
                  >
                    {filter}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            className="
              mt-8 grid grid-cols-2 gap-3
              sm:gap-4
              lg:grid-cols-5
            "
          >
            {filteredImages.map(
              ({ src, category, title }, index) => (
                <article
                  key={`${src}-${index}-${activeFilter}`}
                  className={`
                    event-gallery-card group relative
                    overflow-hidden rounded-[14px]
                    border shadow-[0_10px_30px_rgba(43,0,0,0.08)]
                    ${
                      index === 0
                        ? "col-span-2 lg:col-span-1"
                        : ""
                    }
                  `}
                  style={{ borderColor }}
                >
                  <div
                    className={`
                      relative overflow-hidden
                      ${
                        index === 0
                          ? "h-[220px] sm:h-[280px] lg:h-[215px]"
                          : "h-[175px] sm:h-[235px] lg:h-[215px]"
                      }
                    `}
                  >
                    <img
                      src={src}
                      alt={title}
                      loading="lazy"
                      className="
                        h-full w-full object-cover
                        transition-transform duration-700
                        group-hover:scale-110
                      "
                    />

                    <div
                      className="
                        absolute inset-0
                        bg-gradient-to-t
                        from-[#190000]/85
                        via-transparent
                        to-transparent
                        opacity-30
                        transition-opacity duration-500
                        group-hover:opacity-100
                      "
                    />

                    <div
                      className="
                        absolute inset-x-0 bottom-0
                        translate-y-full p-4
                        transition-transform duration-500
                        group-hover:translate-y-0
                      "
                    >
                      <p
                        className="
                          text-[9px] font-semibold uppercase
                          tracking-[0.2em]
                        "
                        style={{ color: GOLD_LIGHT }}
                      >
                        {category}
                      </p>

                      <h3 className="mt-1 font-serif text-sm text-white">
                        {title}
                      </h3>
                    </div>

                    <div
                      className="
                        absolute right-3 top-3 flex
                        h-8 w-8 scale-0 items-center
                        justify-center rounded-full
                        border border-white/40 bg-white/15
                        text-white opacity-0 backdrop-blur-md
                        transition-all duration-500
                        group-hover:scale-100
                        group-hover:opacity-100
                      "
                    >
                      <ArrowRight size={14} />
                    </div>
                  </div>
                </article>
              )
            )}
          </div>

          {filteredImages.length === 0 && (
            <div
              className="
                mt-8 rounded-2xl border
                px-5 py-16 text-center
              "
              style={{ borderColor }}
            >
              <Trophy
                size={42}
                className="mx-auto"
                style={{ color: GOLD }}
              />

              <p
                className="mt-4 font-serif text-2xl"
                style={{ color: MAROON_DARK }}
              >
                No events available
              </p>
            </div>
          )}

          {/* CTA */}
          <div
            ref={ctaRef}
            className="
              relative mt-8 overflow-hidden
              rounded-[18px] px-5 py-6
              shadow-[0_22px_60px_rgba(43,0,0,0.2)]
              sm:px-8 sm:py-7
              lg:flex lg:items-center
              lg:justify-between lg:px-12
            "
            style={{
              background:
                "linear-gradient(110deg, #190000 0%, #3A0303 55%, #220000 100%)",
            }}
          >
            <div
              className="
                pointer-events-none absolute
                -right-12 -top-24 h-56 w-56
                rounded-full blur-3xl
              "
              style={{
                backgroundColor: "rgba(214,179,63,0.12)",
              }}
            />

            <div
              className="
                relative flex items-start gap-4
                sm:items-center sm:gap-6
              "
            >
              <div
                className="
                  flex h-16 w-16 shrink-0
                  items-center justify-center rounded-full
                  border sm:h-20 sm:w-20
                "
                style={{
                  borderColor: `${GOLD}80`,
                  backgroundColor: "rgba(214,179,63,0.08)",
                  color: GOLD_LIGHT,
                }}
              >
                <Trophy size={35} strokeWidth={1.3} />
              </div>

              <div>
                <h3
                  className="
                    font-serif text-xl
                    sm:text-2xl
                  "
                  style={{ color: GOLD_LIGHT }}
                >
                  Proud Moments, Precious Recognition
                </h3>

                <p
                  className="
                    mt-2 max-w-[650px]
                    text-xs leading-5 text-white/70
                    sm:text-sm sm:leading-6
                  "
                >
                  Every award is a reminder of the trust you place in us.
                  We dedicate these achievements to you.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="
                group relative mt-5 flex w-full
                items-center justify-center gap-3
                rounded-[10px] border px-5 py-3.5
                text-sm font-semibold
                transition-all duration-300
                hover:-translate-y-1
                lg:mt-0 lg:w-auto lg:min-w-[245px]
              "
              style={{
                borderColor: `${GOLD}A0`,
                color: GOLD_LIGHT,
                backgroundColor: "rgba(214,179,63,0.04)",
              }}
            >
              Explore Our Collections

              <ArrowRight
                size={17}
                className="
                  transition-transform duration-300
                  group-hover:translate-x-1
                "
              />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
